import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import add from "../../../../assets/add.png";
import wrong from "../../../../assets/cancel.png";
import correct from "../../../../assets/checkmark.png";
import useFansUnitedSDK from "../../../../hooks/useFansUnitedSDK";

import { MarketTitle, MarketType, MatchStatus } from "../../../../Utils";
import { getShowOddsInMatchCard } from "../../../../redux/user/selectors";
import styles from "./BonusFooter.module.css";

// Updated OddsDisplay component using the refined style
const OddsDisplay = ({ odds, match, t }) => {
	// If odds are not loaded yet, show loading state
	if (!odds) {
		return <div className={styles.loadingOdds}>{t("predictions.bonusFooter.loadingOdds")}</div>;
	}

	const getOddsLabel = (selection, match, t) => {
		const labels = {
			1: match?.homeTeam?.code,
			x: t("predictions.bonusFooter.draw"),
			2: match?.awayTeam?.code,
		};
		return labels[selection?.name] || t("predictions.bonusFooter.unknown");
	};

	const getOddsUrl = (selection) => {
		const urls = selection?.urls;
		if (urls && Array.isArray(urls)) {
			const mobileUrl = urls.find((url) => url.appType === "mobile");
			return mobileUrl?.url || null;
		}
		return null;
	};

	return (
		<>
			<div className={styles.oddsHeader}>
				<div className={styles.oddsTitle}>{t("predictions.bonusFooter.oddsToWin")}</div>
			</div>
			{/* The odds boxes below the labels */}
			<table className={styles.oddsContainer}>
				<tbody>
					<tr>
						{odds.map((selection) => {
							return (
								<td
									key={selection.id}
									className={styles.oddsBox}
									onClick={() => {
										const url = getOddsUrl(selection);
										if (url) {
											window.open(url, "_blank");
										}
									}}
								>
									<div className={styles.oddsLabel}>{getOddsLabel(selection, match, t)}</div>
									<div className={styles.oddsValue}>{Number(selection.odds).toFixed(2)}</div>
								</td>
							);
						})}
					</tr>
				</tbody>
			</table>
		</>
	);
};

const BonusFooter = ({ match, markets }) => {
	const { t } = useTranslation();
	const fansUnitedSDK = useFansUnitedSDK();
	const [odds, setOdds] = React.useState(null);
	const showOddsInMatchCard = useSelector(getShowOddsInMatchCard);

	useEffect(() => {
		if (fansUnitedSDK && match && showOddsInMatchCard) {
			getOddsForMatch([match.id]);
		}
	}, [fansUnitedSDK, match, showOddsInMatchCard]);

	const canAddPrediction = match.status.type !== MatchStatus.FINISHED && match.status.type !== MatchStatus.IN_PROGRESS;

	const getOddsForMatch = async (matchId) => {
		const odds = await fansUnitedSDK.odds.getByMatchIds(matchId, { marketTypes: ["FT_1X2"] });
		if (odds && odds.size > 0) {
			// Get the first entry from the Map
			const [_, matchOdds] = [...odds][0];

			if (matchOdds && matchOdds[0]?.markets) {
				const market = matchOdds[0].markets.find((m) => m.type.code === "1x2");
				if (market && market.selections) {
					setOdds(market.selections);
				}
			}
		}
	};

	const renderPrediction = (market) => {
		const hasResult = market.result?.status === "SETTLED" || market.result?.status === "RESETTLED";
		const isCorrect = hasResult && market.result?.outcome === "CORRECT";

		if (market.type === MarketType.PLAYER_SCORE_FIRST_GOAL) {
			return (
				<div className={styles.iconContainer}>
					<div className={styles.resultText}>{market.prediction?.name}</div>
					{hasResult && <img className={styles.statusIcon} src={isCorrect ? correct : wrong} alt={"status-icon"} />}
				</div>
			);
		}
		return null;
	};

	// Filter to only show the PLAYER_SCORE_FIRST_GOAL market
	const filteredMarkets = markets.filter((market) => market.type === MarketType.PLAYER_SCORE_FIRST_GOAL);

	if (filteredMarkets.length === 0 || !showOddsInMatchCard) {
		return null;
	}

	return (
		<div>
			<div className={styles.rowsContainer}>
				{filteredMarkets.map((market) => {
					const hasPrediction = market.prediction !== null;

					const rowStyleClasses = [styles.row];
					hasPrediction
						? rowStyleClasses.push(styles.rowApplied)
						: !canAddPrediction
							? rowStyleClasses.push(styles.rowDisabled)
							: rowStyleClasses.push("");

					return (
						<div key={market.type} className={rowStyleClasses.join(" ")}>
							<div className={styles.rowText}>{t(MarketTitle[market.type])}</div>
							{hasPrediction ? renderPrediction(market) : <img className={styles.rowIcon} src={add} alt={"add-icon"} />}
						</div>
					);
				})}
			</div>

			{/* Add the updated OddsDisplay component - only show if enabled by region config */}
			{showOddsInMatchCard && odds && <OddsDisplay odds={odds} match={match} t={t} />}
		</div>
	);
};

export default BonusFooter;
